// Client for /api/listings/{id}/detail. Mirrors the backend
// `ListingDetailDTO` in src/rentallens/api/listings.py.

export interface ListingSnapshotDTO {
  id: string;
  scraped_at: string;
  monthly_rent_dkk: number | null;
  deposit_dkk: number | null;
  prepaid_rent_dkk: number | null;
  aconto_dkk: number | null;
  rooms: number | null;
  area_m2: number | null;
  floor: string | null;
  property_type: string | null;
  address_raw: string | null;
  postal_code: string | null;
  city: string | null;
  available_from: string | null;
  title: string | null;
  description: string | null;
  latitude: number | null;
  longitude: number | null;
  energy_label: string | null;
  image_count: number | null;
  image_urls: string[] | null;
}

export interface ListingUnitProfileDTO {
  unit_id: string;
  bbr_unit_id: string | null;
  address: string | null;
  floor: string | null;
  door: string | null;
  area_m2: number | null;
  rooms: number | null;
  construction_year: number | null;
}

export interface ListingDetailDTO {
  id: string;
  url: string;
  portal_slug: string | null;
  portal_name: string | null;
  status: string;
  in_scope: boolean;
  address_precision: string;
  floor_source: string;
  confidence_score: number;
  comparables_eligible: boolean;
  address_confidence: string | null;
  first_seen_at: string;
  last_seen_at: string;
  snapshots: ListingSnapshotDTO[];
  unit: ListingUnitProfileDTO | null;
}

export async function fetchListingDetail(
  id: string
): Promise<ListingDetailDTO | null> {
  const res = await fetch(`/api/listings/${encodeURIComponent(id)}/detail`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`listing_detail_${res.status}`);
  return (await res.json()) as ListingDetailDTO;
}

export interface ComparableRow {
  listing_id: string;
  url: string | null;
  portal_slug: string | null;
  address: string | null;
  postal_code: string | null;
  city: string | null;
  area_m2: number | null;
  rooms: number | null;
  floor: string | null;
  monthly_rent_dkk: number | null;
  price_per_sqm: number | null;
  distance_m: number | null;
  similarity_score: number;
}

export type RentVerdictBucket =
  | 'very_cheap'
  | 'cheap'
  | 'fair'
  | 'expensive'
  | 'overpriced'
  | 'unknown';

export interface RentVerdict {
  bucket: RentVerdictBucket;
  diff_pct: number | null;
  target_price_per_sqm: number | null;
  median_price_per_sqm: number | null;
  p25_price_per_sqm: number | null;
  p75_price_per_sqm: number | null;
  n: number;
}

export interface ComparablesResponse {
  target_listing_id: string;
  items: ComparableRow[];
  verdict: RentVerdict;
}

export async function fetchListingComparables(
  id: string,
  n = 10
): Promise<ComparablesResponse | null> {
  const params = new URLSearchParams({ n: String(n) });
  const res = await fetch(
    `/api/listings/${encodeURIComponent(id)}/comparables?${params.toString()}`
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`listing_comparables_${res.status}`);
  return (await res.json()) as ComparablesResponse;
}
